import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import { CalendarWithViews } from './CalendarWithViews'
import {
  Calendar, Clock, MapPin, Bus, Car, AlertCircle,
  Info, Plus, School, Users, ChevronLeft, ChevronRight,
  Phone, Globe, Navigation, CheckCircle, XCircle, Loader,
  Home, BookOpen
} from 'lucide-react'
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval,
         isToday, isSameMonth, addMonths, subMonths, isSameDay,
         startOfWeek, endOfWeek, getDay, isWeekend } from 'date-fns'
import toast from 'react-hot-toast'
import { formatTimeString } from '@/utils/helpers'
import { useCustodyParents } from '@/hooks/useCustodyParents'

// Debug flag - can be enabled via localStorage
// Set in browser console: localStorage.setItem('debug_school_calendar', 'true')
// Disable: localStorage.removeItem('debug_school_calendar')
const DEBUG_SCHOOL_CALENDAR = localStorage.getItem('debug_school_calendar') === 'true'

interface SchoolEvent {
  id: string
  school_name: string
  event_date: string
  event_type: string
  name: string
  description?: string
  early_release_time?: string
  created_at?: string
}

interface SchoolInfo {
  user: {
    username: string
    display_name: string
    transport_mode?: string
    bus_number?: string
  }
  school: {
    id: string
    name: string
    address?: string
    phone?: string
    website?: string
    district?: string
    calendar_url?: string
  }
  schedule: {
    school_start?: string
    school_end?: string
    bus_pickup?: string
    bus_dropoff?: string
    car_pickup?: string
    car_dropoff?: string
  }
}

interface TransportDay {
  date: string
  morning_method?: string | null
  morning_time?: string | null
  morning_notes?: string | null
  afternoon_method?: string | null
  afternoon_time?: string | null
  afternoon_notes?: string | null
  is_override?: boolean
  reason?: string | null
  at_parent1_house?: boolean | null
}

interface PickupRequest {
  id: string
  request_date: string
  request_time?: string
  request_type: string
  reason: string
  status: string
  created_at: string
}

interface SchoolCalendarViewProps {
  selectedCalendars?: string[];
}

export const SchoolCalendarView: React.FC<SchoolCalendarViewProps> = ({ selectedCalendars = [] }) => {
  const { user } = useAuth()
  // at_parent1_house is true when the kid is with the primary custody parent
  const custodyParents = useCustodyParents()
  const primaryHouse = `${custodyParents.primary}'s`
  const otherHouse = `${custodyParents.other}'s`
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null)
  const [schoolEvents, setSchoolEvents] = useState<SchoolEvent[]>([])
  const [transportSchedule, setTransportSchedule] = useState<TransportDay[]>([])
  const [pickupRequests, setPickupRequests] = useState<PickupRequest[]>([])
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [showPickupRequest, setShowPickupRequest] = useState(false)
  const [loading, setLoading] = useState(true)
  const [newEvent, setNewEvent] = useState({
    name: '',
    event_type: 'other',
    event_date: '',
    description: '',
    early_release_time: ''
  })
  const [newPickupRequest, setNewPickupRequest] = useState({
    date: '',
    time: '',
    request_type: 'after_school',
    reason: ''
  })
  const [selectedEvent, setSelectedEvent] = useState<SchoolEvent | null>(null)
  const [showEventDetails, setShowEventDetails] = useState(false)

  useEffect(() => {
    // Clear previous data when component mounts or user changes
    setSchoolInfo(null)
    setSchoolEvents([])
    setTransportSchedule([])
    setPickupRequests([])
    
    if (user?.id) {
      loadSchoolData()
    }
  }, [user?.id])
  
  // Separate effect for month changes
  useEffect(() => {
    if (user?.id && schoolInfo) {
      loadSchoolData()
    }
  }, [currentMonth])

  const loadSchoolData = async () => {
    try {
      setLoading(true)
      
      // Load school info
      const schoolData = await apiClient.get(`/api/school-transport/school-info/${user.id}`)
      setSchoolInfo(schoolData)
      
      // Load school events - pass user_id for server-side filtering
      // Calculate days_back to show events from start of displayed month
      const today = new Date()
      const monthStart = startOfMonth(currentMonth)
      const daysBackToMonth = Math.max(0, Math.ceil((today.getTime() - monthStart.getTime()) / (1000 * 60 * 60 * 24)))

      try {
        const events = await apiClient.get('/api/schools/events', {
          params: { user_id: user?.id, days_back: daysBackToMonth }
        })

        // Map the date field to event_date field that the component expects
        const mappedEvents = (Array.isArray(events) ? events : []).map((e: any) => ({
          ...e,
          event_date: e.date || e.event_date,
          school_name: e.school || e.school_name || e.school_system || 'All Schools',
          event_type: e.type || e.event_type || 'other',
          name: e.name || e.title || 'School Event'
        }))
        .sort((a, b) => {
          // Sort by event_date in chronological order (earliest first)
          const dateA = new Date(a.event_date + 'T12:00:00').getTime()
          const dateB = new Date(b.event_date + 'T12:00:00').getTime()
          return dateA - dateB
        })

        setSchoolEvents(mappedEvents)
      } catch (error) {
        console.error('Failed to load school events:', error)
        setSchoolEvents([])
      }

      // Load transport schedule through end of school year
      const startDate = startOfMonth(currentMonth)
      // Load through May 31st of the current or next school year
      const currentYear = currentMonth.getFullYear()
      const schoolYearEnd = new Date(
        currentMonth.getMonth() >= 7 ? currentYear + 1 : currentYear,
        4, // May (0-indexed)
        31
      )
      const endDate = schoolYearEnd
      const transportURL = `/api/school-transport/transport/schedule/${user.id}?date_from=${format(startDate, 'yyyy-MM-dd')}&date_to=${format(endDate, 'yyyy-MM-dd')}`
      const transport = await apiClient.get(transportURL)

      // Backend returns array directly, not wrapped in {schedule: [...]}
      const scheduleArray = Array.isArray(transport) ? transport : (transport?.schedule || [])
      setTransportSchedule(scheduleArray)
      
      // Load pickup requests
      const requests = await apiClient.get('/api/school-transport/pickup-requests')
      setPickupRequests(requests || [])
      
    } catch (error) {
      console.error('Failed to load school data:', error)
      setSchoolInfo(null)
      toast.error('Failed to load school information')
    } finally {
      setLoading(false)
    }
  }

  const handleAddEvent = async () => {
    if (!newEvent.name || !newEvent.event_date) {
      toast.error('Please fill in required fields')
      return
    }
    
    try {
      await apiClient.post('/api/schools/events', {
        ...newEvent,
        school_id: schoolInfo?.school?.id,
        all_day: !newEvent.early_release_time
      })
      toast.success('Event added successfully!')
      setShowAddEvent(false)
      setNewEvent({
        name: '',
        event_type: 'other',
        event_date: '',
        description: '',
        early_release_time: ''
      })
      loadSchoolData()
    } catch (error) {
      toast.error('Failed to add event')
    }
  }

  const handlePickupRequest = async () => {
    if (!newPickupRequest.date || !newPickupRequest.reason) {
      toast.error('Please fill in required fields')
      return
    }
    
    try {
      await apiClient.post('/api/school-transport/pickup-requests', newPickupRequest)
      toast.success('Pickup request sent to parents!')
      setShowPickupRequest(false)
      setNewPickupRequest({
        date: '',
        time: '',
        request_type: 'after_school',
        reason: ''
      })
      loadSchoolData()
    } catch (error) {
      toast.error('Failed to submit pickup request')
    }
  }

  const getTransportForDate = (date: Date): TransportDay | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return transportSchedule.find(t => t.date === dateStr)
  }

  const getEventsForDate = (date: Date): SchoolEvent[] => {
    const dateStr = format(date, 'yyyy-MM-dd')
    const filtered = schoolEvents.filter(e => e.event_date === dateStr)
    // Debug logging for October dates with events
    if (filtered.length > 0) {
      console.log(`[getEventsForDate] ${dateStr} has ${filtered.length} events:`, filtered.map(e => e.name))
    }
    return filtered
  }

  const getPickupRequestForDate = (date: Date): PickupRequest | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return pickupRequests.find(r => r.request_date === dateStr)
  }

  const isSchoolDay = (date: Date, events: SchoolEvent[]): boolean => {
    // Check if it's a weekend
    if (isWeekend(date)) return false
    
    // Check if there's a no_school or holiday event
    const hasNoSchool = events.some(e => 
      e.event_type === 'no_school' || 
      e.event_type === 'holiday' ||
      e.event_type === 'break'
    )
    
    return !hasNoSchool
  }

  const isDigitalLearningDay = (events: SchoolEvent[]): boolean => {
    return events.some(e => 
      e.event_type === 'digital_learning' || 
      e.name.toLowerCase().includes('digital') ||
      e.name.toLowerCase().includes('remote') ||
      e.name.toLowerCase().includes('virtual')
    )
  }

  const renderCalendarDay = (date: Date) => {
    const transport = getTransportForDate(date)
    const events = getEventsForDate(date)
    const pickupRequest = getPickupRequestForDate(date)
    const isCurrentMonth = isSameMonth(date, currentMonth)
    const isSelected = selectedDate && isSameDay(date, selectedDate)
    const schoolDay = isSchoolDay(date, events)
    const digitalDay = isDigitalLearningDay(events)

    // Debug logging for October 7 specifically
    if (DEBUG_SCHOOL_CALENDAR) {
      const dateStr = format(date, 'yyyy-MM-dd')
      if (dateStr === '2025-10-07' && transport) {
        console.log('[SchoolCalendarView] Oct 7 transport object:', transport)
        console.log('[SchoolCalendarView] Oct 7 fields:', {
          morning_method: transport.morning_method,
          afternoon_method: transport.afternoon_method,
          at_parent1_house: transport.at_parent1_house,
          schoolDay,
          digitalDay
        })
      }
    }

    return (
      <div
        key={date.toISOString()}
        onClick={() => setSelectedDate(date)}
        className={`
          min-h-[100px] p-2 border rounded-lg cursor-pointer transition-all
          ${!isCurrentMonth ? 'opacity-40' : ''}
          ${isToday(date) ? 'bg-blue-50 border-blue-400' : 'border-gray-200'}
          ${isSelected ? 'ring-2 ring-blue-500' : ''}
          ${!schoolDay && isCurrentMonth ? 'bg-gray-50' : ''}
          hover:shadow-md
        `}
      >
        <div className="font-semibold text-sm mb-1 flex items-center justify-between">
          <span>{format(date, 'd')}</span>
          {digitalDay && (
            <BookOpen className="w-3 h-3 text-purple-600" title="Digital Learning Day" />
          )}
        </div>
        
        {/* Transport Mode - Only show on school days */}
        {transport && schoolDay && !digitalDay && (
          <div className="text-xs mb-1">
            {console.log(`[RENDER ${dateStr}] transport:`, transport, 'morning:', transport.morning_method, 'afternoon:', transport.afternoon_method, 'schoolDay:', schoolDay, 'digitalDay:', digitalDay)}
            {/* Show icons based on morning and afternoon methods - ALWAYS show both icons */}
            {transport.morning_method === 'bus' && transport.afternoon_method === 'bus' && (
              <span className="text-lg" title="Bus morning and afternoon">🚌🚌</span>
            )}
            {transport.morning_method === 'parent_car' && transport.afternoon_method === 'parent_car' && (
              <span className="text-lg" title="Car morning and afternoon">🚗🚗</span>
            )}
            {transport.morning_method === 'bus' && transport.afternoon_method === 'parent_car' && (
              <span className="text-lg" title="Bus morning, Car afternoon">🚌🚗</span>
            )}
            {transport.morning_method === 'parent_car' && transport.afternoon_method === 'bus' && (
              <span className="text-lg" title="Car morning, Bus afternoon">🚗🚌</span>
            )}
            {transport.morning_method === 'drives_self' || transport.afternoon_method === 'drives_self' ? (
              <span className="text-lg" title="Drives self">🚙</span>
            ) : null}
            {(transport.morning_method === 'friend_pickup' || transport.afternoon_method === 'friend_pickup') && (
              <div className="flex items-center text-purple-600">
                <Users className="w-3 h-3 mr-1" />
                <span className="text-xs">Friend</span>
              </div>
            )}
          </div>
        )}

        {/* Digital Learning Day indicator */}
        {digitalDay && (
          <div className="text-xs bg-purple-100 text-purple-700 rounded px-1 py-0.5 mb-1">
            Digital Day
          </div>
        )}
        
        {/* School Events */}
        {events.map(event => (
          <div key={event.id} className={`text-xs rounded px-1 py-0.5 mb-1 ${
            event.event_type === 'holiday' || event.event_type === 'no_school' 
              ? 'bg-red-100 text-red-700' 
              : event.event_type === 'early_release'
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-orange-100 text-orange-700'
          }`}>
            {event.name}
          </div>
        ))}
        
        {/* Pickup Request */}
        {pickupRequest && (
          <div className={`text-xs rounded px-1 py-0.5 ${
            pickupRequest.status === 'approved' ? 'bg-green-100' : 
            pickupRequest.status === 'pending' ? 'bg-yellow-100' : 
            'bg-red-100'
          }`}>
            Pickup {pickupRequest.status}
          </div>
        )}

        {/* At Parent's House indicator - show for school days with custody info */}
        {transport && schoolDay && transport.at_parent1_house !== null && transport.at_parent1_house !== undefined && (
          <div className="text-[10px] text-gray-500 mt-1">
            {transport.at_parent1_house ? (
              <span className="flex items-center">
                <Home className="w-3 h-3 mr-1" />
                {primaryHouse} House
              </span>
            ) : (
              <span>{otherHouse} House</span>
            )}
          </div>
        )}
      </div>
    )
  }

  // Generate calendar days with proper week alignment
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const startDate = startOfWeek(monthStart) // Start from Sunday
    const endDate = endOfWeek(monthEnd) // End on Saturday
    
    return eachDayOfInterval({ start: startDate, end: endDate })
  }

  const calendarDays = generateCalendarDays()

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2">Loading school calendar for {user?.username}...</span>
      </div>
    )
  }

  // If no school info loaded, show error state
  if (!schoolInfo) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-900 mb-2">No School Information Found</h3>
        <p className="text-red-700">Unable to load school information for {user?.display_name || user?.username}.</p>
        <button 
          onClick={loadSchoolData}
          className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with School Info */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <School className="w-6 h-6 mr-2" />
              {schoolInfo?.school?.name || 'My School'}
            </h2>
            <p className="text-gray-600 mt-1">{schoolInfo?.school?.district} School District</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowPickupRequest(true)}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center justify-center text-sm sm:text-base"
            >
              <Car className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="whitespace-nowrap">Request Pickup</span>
            </button>
            <button
              onClick={() => setShowAddEvent(true)}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="whitespace-nowrap">Add Event</span>
            </button>
          </div>
        </div>

        {/* School Contact Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {schoolInfo?.school?.phone && (
            <div className="flex items-center text-gray-600 truncate">
              <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{schoolInfo.school.phone}</span>
            </div>
          )}
          {schoolInfo?.school?.website && (
            <a 
              href={schoolInfo.school.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:underline truncate"
            >
              <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">School Website</span>
            </a>
          )}
          {schoolInfo?.school?.address && (
            <div className="flex items-start text-gray-600 col-span-1 sm:col-span-2 md:col-span-1">
              <MapPin className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm">{schoolInfo.school.address}</span>
            </div>
          )}
        </div>

        {/* Schedule Times */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-sm mb-2">
            {user?.username === 'john' ? 'Lost Mountain Schedule' : 
             user?.username === 'mo' ? 'Kell High School Schedule' :
             user?.username === 'gus' ? 'Chesterton Academy Schedule' :
             user?.username === 'james' ? 'Sammy McClure Schedule' : 'Your Schedule'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {user?.username === 'john' && (
              <>
                <div>
                  <span className="text-gray-500">Arrival Starts:</span>
                  <div className="font-medium">8:30 AM</div>
                </div>
                <div>
                  <span className="text-gray-500">Tardy Bell:</span>
                  <div className="font-medium text-red-600">9:10 AM</div>
                </div>
                <div>
                  <span className="text-gray-500">Checkout:</span>
                  <div className="font-medium">3:45 PM</div>
                </div>
                <div>
                  <span className="text-gray-500">Dismissal:</span>
                  <div className="font-medium">4:15 PM</div>
                </div>
              </>
            )}
            
            {(schoolInfo?.user?.transport_mode === 'bus' || user?.username === 'john') && (
              <>
                <div className="col-span-2 md:col-span-4 mt-3 pt-3 border-t">
                  <h4 className="font-semibold text-sm mb-2 text-yellow-600">🚌 Bus Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <span className="text-gray-500">Bus Number:</span>
                      <div className="font-bold text-lg">#{schoolInfo?.user?.bus_number || '5107'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Morning Pickup:</span>
                      <div className="font-medium">8:26 AM</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Afternoon Drop-off:</span>
                      <div className="font-medium">4:40 PM</div>
                    </div>
                    {user?.username === 'john' && (
                      <div>
                        <span className="text-gray-500">Schedule:</span>
                        <div className="font-medium text-xs">
                          Bus at {primaryHouse}, varies at {otherHouse}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Calendar with View Options */}
      <CalendarWithViews
        events={[
          ...schoolEvents.map(event => ({
            date: event.event_date,
            title: event.name,
            type: event.event_type,
            description: event.description,
            color: event.event_type === 'holiday' || event.event_type === 'no_school' 
              ? 'bg-red-100 text-red-700' 
              : event.event_type === 'early_release'
              ? 'bg-yellow-100 text-yellow-700'
              : event.event_type === 'digital_learning'
              ? 'bg-purple-100 text-purple-700'
              : 'bg-orange-100 text-orange-700',
            onClick: () => {
              setSelectedEvent(event)
              setShowEventDetails(true)
            }
          })),
          ...transportSchedule.map(transport => ({
            date: transport.date,
            title: transport.morning_method === 'bus' && transport.afternoon_method === 'bus'
              ? '🚌'
              : transport.morning_method === 'parent_car' && transport.afternoon_method === 'parent_car'
              ? '🚗'
              : transport.morning_method !== transport.afternoon_method
              ? transport.morning_method === 'bus' ? '🚌🚗' : '🚗🚌'
              : transport.morning_method === 'drives_self' || transport.afternoon_method === 'drives_self'
              ? '🚙'
              : '👥',
            type: 'transport',
            description: transport.at_parent1_house !== null && transport.at_parent1_house !== undefined
              ? (transport.at_parent1_house ? `At ${primaryHouse}` : `At ${otherHouse}`)
              : '',
            color: transport.morning_method === 'bus' || transport.afternoon_method === 'bus'
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-blue-100 text-blue-700'
          }))
        ]}
        onDateSelect={setSelectedDate}
        renderDayContent={(date, events) => {
          const transport = getTransportForDate(date)
          const schoolEvts = getEventsForDate(date)
          const pickupRequest = getPickupRequestForDate(date)
          const schoolDay = isSchoolDay(date, schoolEvts)
          const digitalDay = isDigitalLearningDay(schoolEvts)
          
          return (
            <div className="space-y-0.5">
              {/* Transport Mode - Only show on school days */}
              {transport && schoolDay && !digitalDay && (
                <div className="text-sm">
                  {transport.morning_method === 'bus' && transport.afternoon_method === 'bus' && (
                    <span title="Bus morning and afternoon">🚌🚌</span>
                  )}
                  {transport.morning_method === 'parent_car' && transport.afternoon_method === 'parent_car' && (
                    <span title="Car morning and afternoon">🚗🚗</span>
                  )}
                  {transport.morning_method === 'bus' && transport.afternoon_method === 'parent_car' && (
                    <span title="Bus morning, Car afternoon">🚌🚗</span>
                  )}
                  {transport.morning_method === 'parent_car' && transport.afternoon_method === 'bus' && (
                    <span title="Car morning, Bus afternoon">🚗🚌</span>
                  )}
                </div>
              )}

              {/* Digital Learning Day indicator */}
              {digitalDay && (
                <div className="text-[10px] bg-purple-100 text-purple-700 rounded px-1">
                  Digital
                </div>
              )}

              {/* School Events */}
              {schoolEvts.slice(0, 1).map(event => (
                <div key={event.id} className={`text-[10px] rounded px-1 truncate ${
                  event.event_type === 'holiday' || event.event_type === 'no_school'
                    ? 'bg-red-100 text-red-700'
                    : event.event_type === 'early_release'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {event.name.substring(0, 10)}
                </div>
              ))}

              {/* Pickup Request */}
              {pickupRequest && (
                <div className={`text-[10px] rounded px-1 ${
                  pickupRequest.status === 'approved' ? 'bg-green-100' :
                  pickupRequest.status === 'pending' ? 'bg-yellow-100' :
                  'bg-red-100'
                }`}>
                  Pickup
                </div>
              )}

              {/* At Parent's House indicator */}
              {transport && transport.at_parent1_house !== null && transport.at_parent1_house !== undefined && (
                <div className="text-[10px] text-gray-500">
                  {transport.at_parent1_house ? primaryHouse : otherHouse}
                </div>
              )}
            </div>
          )
        }}
      />

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="flex items-center">
            <Bus className="w-4 h-4 mr-1 text-yellow-600" />
            <span>Bus Day</span>
          </div>
          <div className="flex items-center">
            <Car className="w-4 h-4 mr-1 text-blue-600" />
            <span>Parent Pickup</span>
          </div>
          <div className="flex items-center">
            <BookOpen className="w-4 h-4 mr-1 text-purple-600" />
            <span>Digital Learning</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 mr-1 bg-red-100 rounded"></div>
            <span>No School</span>
          </div>
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold text-lg mb-4">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h3>
          
          {/* Transport for selected date */}
          {(() => {
            const transport = getTransportForDate(selectedDate)
            const events = getEventsForDate(selectedDate)
            const pickupRequest = getPickupRequestForDate(selectedDate)
            const schoolDay = isSchoolDay(selectedDate, events)
            const digitalDay = isDigitalLearningDay(events)
            
            return (
              <>
                {!schoolDay && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-700">No School</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {isWeekend(selectedDate) ? 'Weekend' : 'School Holiday/Break'}
                    </div>
                  </div>
                )}

                {digitalDay && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
                      <span className="font-medium text-purple-900">Digital Learning Day</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Stay home and log into school online
                    </div>
                  </div>
                )}

                {transport && schoolDay && !digitalDay && (
                  <div className="mb-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Transportation</h4>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          {/* Show icons based on morning/afternoon methods */}
                          {transport.morning_method === 'bus' && transport.afternoon_method === 'bus' && (
                            <Bus className="w-5 h-5 mr-2 text-yellow-600" />
                          )}
                          {transport.morning_method === 'parent_car' && transport.afternoon_method === 'parent_car' && (
                            <Car className="w-5 h-5 mr-2 text-blue-600" />
                          )}
                          {transport.morning_method !== transport.afternoon_method && (
                            <>
                              {transport.morning_method === 'bus' && <Bus className="w-5 h-5 mr-1 text-yellow-600" />}
                              {transport.morning_method === 'parent_car' && <Car className="w-5 h-5 mr-1 text-blue-600" />}
                              {transport.afternoon_method === 'bus' && <Bus className="w-5 h-5 mr-2 text-yellow-600" />}
                              {transport.afternoon_method === 'parent_car' && <Car className="w-5 h-5 mr-2 text-blue-600" />}
                            </>
                          )}
                          <span className="font-medium capitalize">
                            {transport.morning_method === transport.afternoon_method
                              ? transport.morning_method?.replace('_', ' ') || 'Unknown'
                              : 'Mixed Transportation'}
                          </span>
                        </div>
                        {transport.at_parent1_house !== null && transport.at_parent1_house !== undefined && (
                          <div className="text-sm text-gray-600">
                            {transport.at_parent1_house ? `At ${primaryHouse} House` : `At ${otherHouse} House`}
                          </div>
                        )}
                      </div>

                      {/* Show morning transport details */}
                      {transport.morning_method && (
                        <div className="text-sm text-gray-600">
                          <strong>Morning:</strong> {transport.morning_method.replace('_', ' ')}
                          {transport.morning_time && ` at ${transport.morning_time}`}
                        </div>
                      )}

                      {/* Show afternoon transport details */}
                      {transport.afternoon_method && (
                        <div className="text-sm text-gray-600">
                          <strong>Afternoon:</strong> {transport.afternoon_method.replace('_', ' ')}
                          {transport.afternoon_time && ` at ${transport.afternoon_time}`}
                        </div>
                      )}

                      {transport.notes?.map((note, idx) => (
                        <div key={idx} className="text-sm text-gray-500 italic mt-1">
                          {note}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {events.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">School Events</h4>
                    {events.map(event => (
                      <div key={event.id} className="bg-orange-50 p-3 rounded-lg mb-2">
                        <div className="font-medium">{event.name}</div>
                        {event.description && (
                          <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                        )}
                        {event.early_release_time && (
                          <div className="text-sm text-gray-500 mt-1">
                            Early Release: {formatTimeString(event.early_release_time)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {pickupRequest && (
                  <div className="mb-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Pickup Request</h4>
                    <div className={`p-3 rounded-lg ${
                      pickupRequest.status === 'approved' ? 'bg-green-50' : 
                      pickupRequest.status === 'pending' ? 'bg-yellow-50' : 
                      'bg-red-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium capitalize">
                            {pickupRequest.request_type.replace('_', ' ')}
                          </div>
                          {pickupRequest.request_time && (
                            <div className="text-sm text-gray-600">
                              Time: {pickupRequest.request_time}
                            </div>
                          )}
                          <div className="text-sm text-gray-600">
                            Reason: {pickupRequest.reason}
                          </div>
                        </div>
                        <div className="flex items-center">
                          {pickupRequest.status === 'approved' && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                          {pickupRequest.status === 'pending' && (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          )}
                          {pickupRequest.status === 'rejected' && (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <span className="ml-2 text-sm font-medium capitalize">
                            {pickupRequest.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add School Event</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Event name"
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <select
                value={newEvent.event_type}
                onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="other">Other</option>
                <option value="holiday">Holiday</option>
                <option value="early_release">Early Release</option>
                <option value="no_school">No School</option>
                <option value="digital_learning">Digital Learning Day</option>
                <option value="event">Special Event</option>
              </select>
              <input
                type="date"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <input
                type="time"
                placeholder="Early release time (optional)"
                value={newEvent.early_release_time}
                onChange={(e) => setNewEvent({ ...newEvent, early_release_time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <textarea
                placeholder="Description (optional)"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowAddEvent(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEvent}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pickup Request Modal */}
      {showPickupRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Request Pickup</h3>
            <div className="space-y-4">
              <select
                value={newPickupRequest.request_type}
                onChange={(e) => setNewPickupRequest({ ...newPickupRequest, request_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="after_school">After School Activity</option>
                <option value="early_dismissal">Early Dismissal</option>
                <option value="late_arrival">Late Arrival</option>
              </select>
              <input
                type="date"
                value={newPickupRequest.date}
                onChange={(e) => setNewPickupRequest({ ...newPickupRequest, date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <input
                type="time"
                placeholder="Pickup time"
                value={newPickupRequest.time}
                onChange={(e) => setNewPickupRequest({ ...newPickupRequest, time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <textarea
                placeholder="Reason for pickup request"
                value={newPickupRequest.reason}
                onChange={(e) => setNewPickupRequest({ ...newPickupRequest, reason: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowPickupRequest(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handlePickupRequest}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">{selectedEvent.name}</h3>
              <button
                onClick={() => {
                  setShowEventDetails(false)
                  setSelectedEvent(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm">
                  {format(parseISO(selectedEvent.event_date), 'EEEE, MMMM d, yyyy')}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <School className="w-4 h-4 text-gray-500" />
                <span className="text-sm">{selectedEvent.school_name || 'All Schools'}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Info className="w-4 h-4 text-gray-500" />
                <span className="text-sm capitalize">
                  {selectedEvent.event_type.replace(/_/g, ' ')}
                </span>
              </div>
              
              {selectedEvent.early_release_time && (
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Early Release: {formatTimeString(selectedEvent.early_release_time)}</span>
                </div>
              )}
              
              {selectedEvent.description && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{selectedEvent.description}</p>
                </div>
              )}
            </div>
            
            <div className="mt-6">
              <button
                onClick={() => {
                  setShowEventDetails(false)
                  setSelectedEvent(null)
                }}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}